import {defineStore} from "pinia";

import {Trip} from "../models/Trip";
import * as TripService from "../services/trip.service";
import {updateTripOffer, deleteTripCrew, getTripOfferDetail, tripOfferAdd} from "../services/trip.service";
import * as DateUtils from "@/helpers/dateUtils";
import {format} from "@/helpers/dateUtils";
import {ref, toRaw, isProxy, nextTick} from "vue";




export const useTripsStore = defineStore('trips', () => {
    const trips = ref<Trip[]>([]);
    const trip = ref<Trip | null>(null);
    const loading = ref(false);
    const boatsInTrips = ref<(string | number)[]>([]);

    const tripImageUrl = ref<string | null>(null);
    const tripImageUrlLoading = (false);
    const payload = ref<any>(null);



    function getTripStatus(trip: Trip): string {
        const statusMap = {
            0: "open",
            1: "offer",
            2: "planned",
            3: "active",
            4: "done",
            5: "cancelled"
        };
        return statusMap[trip.state] || "unknown";
    }

    //Get all trips from API
    async function getAll() {
        if(loading.value){
            return
        }

        loading.value = true;
        try {
            const response = await TripService.getTrips();
            console.log("Raw API Response:", response.data);

            if (Array.isArray(response.data)) {
                trips.value = response.data;
            } else if (response.data._embedded && Array.isArray(response.data._embedded.trips)) {
                trips.value = response.data._embedded.trips;
            } else {
                console.error("Unexpected API response format:", response.data);
                trips.value = [];
            }

            const now = Date.now();
            trips.value = trips.value.map(trip => ({
              ...trip,
              backgroundImageUrl: trip.backgroundImageUrl ? `${trip.backgroundImageUrl}?t=${now}` : null
            }));

            // Ensure each offer carries the trip location object as `locationFrom`
            // and a plain id as `destination` for components that expect it.
            trips.value = trips.value.map((tripItem: any) => {
                const locObj = tripItem?.locationId ?? null; // backend returns object in `locationId`
                const locId = locObj && typeof locObj === 'object' ? locObj.id : locObj;
                return {
                    ...tripItem,
                    offers: (tripItem.offers || []).map((offerItem: any) => ({
                        ...offerItem,
                        locationFrom: offerItem.locationFrom ?? locObj ?? null,
                        destination: offerItem.destination ?? locId ?? null,
                    })),
                };
            });

            if (!trip.value && trips.value.length > 0) {
                trip.value = trips.value[0];
            }


            // Extract unique boat IDs (normalized as strings)
            
                const uniqueIds = new Set<string>();
                trips.value.forEach(tripItem => {
                    (tripItem.offers || []).forEach(offerItem => {
                        const yachtId = offerItem?.yacht?.id ?? offerItem?.yachtId;
                        if (yachtId != null) uniqueIds.add(String(yachtId));
                    });
                });
                console.log("uniqueIds of yachts inside trip", uniqueIds)
                boatsInTrips.value = Array.from(uniqueIds);
                console.log("boatsInTrips.value",  boatsInTrips.value)
            

        } catch (error) {
            console.error("Failed to fetch trips:", error);
            trips.value = [];
        } finally {
            loading.value = false;
        }
    }

    function getAvailable() {
        return trips.value.filter(trip => trip.state !== 4 && trip.state !== 3);
    }

    function isYachtInAnyTrip(yachtId: string | number | null | undefined) {
        const yachtIdString = String(yachtId);
        const result = boatsInTrips.value.some(boatId => String(boatId) === yachtIdString);
        console.log('[isYachtInAnyTrip] Checking yachtId:', yachtIdString, 'against boatsInTrips:', boatsInTrips.value, '=>', result);
        return result;
    }

    function isYachtInTripByUuid(uuid: string, yachtId: string | number | null | undefined) {
        const yachtIdString = String(yachtId);
        const trip = trips.value.find(tripItem => tripItem.uuid === uuid);
        if (!trip) {
            console.log('[isYachtInTripByUuid] Trip not found', { uuid, yachtId: yachtIdString });
            return false;
        }
        const ids = (trip.offers || []).map(offer => String(offer?.yacht?.id ?? offer?.yachtId));
        const result = ids.includes(yachtIdString);
        console.log('[isYachtInTripByUuid]', { uuid, yachtId: yachtIdString, ids, result });
        return result;
    }

    async function createTrip(payload: {
        name: string;
        dateStart: Date | string;
        dateEnd: Date | string;
        locationId?: string | number;
        locationType?: string;
    }) {
        const toYMD = (d: Date | string) =>
            typeof d === "string" ? d : DateUtils.format(d); // -> 'YYYY-MM-DD'

        loading.value = true;
        try {
            const { name, dateStart, dateEnd, locationId, locationType } = payload;

            const tripPayload: Record<string, any> = {
                name,
                dateStart: toYMD(dateStart),
                dateEnd: toYMD(dateEnd),
            };
            if (locationId !== undefined) tripPayload.locationId = String(locationId);
            if (locationType) tripPayload.locationType = locationType;

            console.log("Trip payload being sent to API (minimal):", tripPayload);

            const res = await TripService.createTrip(tripPayload);
            console.log("CreateTrip response:", res?.data);
            return res?.data; // { message, uuid, name }
        } catch (error) {
            console.error("Failed to create trip:", error);
            throw error;
        } finally {
            loading.value = false;
        }
    }

    async function updateTrip(uuid: string, updatedData: Partial<Trip>) {
        loading.value = true;
        try {
            await TripService.updateTrip(uuid, updatedData);
            await getAll();
        } catch (error) {
            console.error(`Failed to update trip ${uuid}:`, error);
        } finally {
            loading.value = false;
        }
    }

    //Load single trip by slug
    async function loadTrip(slug: string) {
        loading.value = true;
        try {
            const response = await TripService.getTrip(slug);
            console.log("LOAD TRIP BY SLUG", response.data);
            if (!response || !response.data) {
                console.error("Trip not found");
                return null;
            }
            // Enrich offers with parent trip location, similar to getAll
            const fetchedTrip = response.data;
            const locObj = fetchedTrip?.locationId ?? null;
            const locId = locObj && typeof locObj === 'object' ? locObj.id : locObj;
            fetchedTrip.offers = (fetchedTrip.offers || []).map((offerItem: any) => ({
                ...offerItem,
                locationFrom: offerItem.locationFrom ?? locObj ?? null,
                destination: offerItem.destination ?? locId ?? null,
                // Backward-compat: keep `location` populated too if API/UI still expects it
                location: offerItem.location ?? locObj ?? null,
            }));
            trip.value = fetchedTrip;

            console.log("Trip loaded in store:", trip.value);
        } catch (error) {
            console.error("Error fetching trip:", error);
            trip.value = null;
        } finally {
            loading.value = false;
        }
    }

    async function removeTrip(uuid: string) {
        loading.value = true;
        try {
            await TripService.deleteTrip(uuid);
            await getAll();
        } catch (error) {
            console.error(`Failed to delete trip ${uuid}:`, error);
        } finally {
            loading.value = false;
        }
    }

    async function updateTripImageStore(file: File, uuid: string) {
        function fileToBase64(file: File): Promise<string> {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result.toString());
                reader.onerror = reject;
                reader.readAsDataURL(file);
            });
        }

        try {
            const base64Image = await fileToBase64(file);

            const response = await TripService.updateTripImage(uuid, base64Image);
            const newImageUrl = response.data;

            tripImageUrl.value = newImageUrl;
            console.log("Updated trip image URL:", newImageUrl);
            return newImageUrl;

        } catch (error) {
            console.error("Trip image upload failed:", error);
            throw error;
        }
    }
    
    async function patchOfferToTrip(uuid: string, offer: any) {
        if (!offer || !offer.yachtId) {
            console.warn('[patchOfferToTrip] Missing or invalid offer payload:', offer);
            return;
        }

        // Store the payload globally
        this.payload = offer;


        try {
            // If trip exists locally, prevent duplicate yacht in that trip
            const idx = trips.value.findIndex(t => t.uuid === uuid);
            console.log("offer is slug or uuid", uuid)
            if (idx !== -1) {
                const trip = trips.value[idx];
                const exists = (trip.offers || []).some(o => (o?.yacht?.id ?? o?.yachtId) == offer.yachtId);
                if (exists) {
                    console.info(`[patchOfferToTrip] Yacht ${offer.yachtId} already in trip "${uuid}".`);
                    return;
                }
            } else {
                console.info(`[patchOfferToTrip] Trip "${uuid}" not found locally — continuing and will refresh store after API call.`);
            }
            
            const response = await tripOfferAdd(uuid, offer);
            const apiOffer = response?.data;

            // Compute location object and id from trip if available
            const tripLocationObj = idx !== -1 ? (trips.value[idx]?.locationId ?? null) : null;
            const tripLocationId = tripLocationObj && typeof tripLocationObj === 'object' ? tripLocationObj.id : tripLocationObj;

            // Normalize and enrich with locationFrom/destination
            let newOffer = apiOffer ?? {
                yacht: { id: offer.yachtId, name: offer.name },
                price: offer.basePrice,
                finalPrice: offer.offerPrice,
                dateStart: offer.dateStart,
                dateEnd: offer.dateEnd,
                destination: offer.destination ?? tripLocationId ?? null,
                amenities: offer.amenities,
                passengers: offer.passengers,
                locationFrom: offer.locationFrom ?? tripLocationObj ?? null,
            };

            // newOffer = {
            //     ...newOffer,
            //     locationFrom: newOffer.locationFrom ?? tripLocationObj ?? null,
            //     destination: newOffer.destination ?? tripLocationId ?? null,
            // };

            // If trip exists in store, append locally; otherwise just refresh everything.
            if (idx !== -1) {
                const trip = trips.value[idx];
                trips.value[idx] = {
                    ...trip,
                    offers: [...(trip.offers || []), newOffer]
                };
            } else {
                await getAll(); // ensure new trip & offers are loaded
            }

            {
              const id = String(offer.yachtId);
              if (!boatsInTrips.value.some(b => String(b) === id)) {
                boatsInTrips.value = [...boatsInTrips.value, id];
              }
            }

            console.log('[patchOfferToTrip] Offer attached successfully:', newOffer);
            return newOffer;
        } catch (err) {
            console.error('[patchOfferToTrip] Failed:', err);
            throw err;
        }
    }

    async function updateTripOffer(uuid: string, offerUuid:string, offer: any) {
        try {
            await TripService.updateTripOffer(uuid, offerUuid, offer);
        } catch (error) {
            console.error(`Failed to update offer in trip ${uuid}:`, error);
            throw error;
        }
    }
    
    async function deleteTripOffer(uuid: string, offerUuid: string) {
        loading.value = true;
        try {
            await TripService.deleteTripOffer(uuid, offerUuid);
            const tripToUpdate = trips.value.find(t => t.uuid === uuid);
            if (tripToUpdate && tripToUpdate.offers) {
                tripToUpdate.offers = tripToUpdate.offers.filter(offer => offer.uuid !== offerUuid);
            }
        } catch (error) {
            console.error(`Failed to delete offer ${offerUuid} from trip ${uuid}:`, error);
        } finally {
            loading.value = false;
        }
    }

    function hasTrips() {
        return trips.value.length > 0;
    }


    async function removeCrewMember(slug: string, crewUuid: string) {
        try {
            await deleteTripCrew(slug, crewUuid);
            if (trip.value && trip.value.slug === slug) {
                trip.value.crewMembers = trip.value.crewMembers.filter(
                    (member) => member.uuid !== crewUuid
                );
            }
        } catch (error) {
            console.error('Failed to delete crew member:', error);
            throw error;
        }
    }


    async function updateTripCrew(uuid: string, crewUuids: string[]) {
        try {
            const payload = { crewUuids };
            const response = await TripService.updateTripCrew(uuid, payload);
            console.log("Updated crew list:", response.data);
            return response.data;
        } catch (error) {
            console.error(`Failed to update crew list in trip ${uuid}:`, error);
            throw error;
        }
    }

    // Normalize amenities into array of IDs
    function normalizeAmenities(input: any): (string | number)[] {
        if (!input) return [];
        if (Array.isArray(input)) {
            return input
                .map((amenity: any) => (typeof amenity === 'object' ? amenity?.id : amenity))
                .filter((v: any) => v !== undefined && v !== null && v !== '');
        }
        if (typeof input === 'string') {
            // comma-separated amenieties
            return input
                .split(',')
                .map(s => s.trim())
                .filter(Boolean);
        }
        return [];
    }

    // Resolve dates from offer/trip into YYYY-MM-DD strings
    function resolveOfferDates(offer: any, fallbackTrip?: Trip | null): { dateFrom?: string; dateTo?: string } {
        const dateFrom = offer?.dateStart ?? offer?.dateFrom ?? fallbackTrip?.dateStart ?? undefined;
        const dateTo   = offer?.dateEnd   ?? offer?.dateTo   ?? fallbackTrip?.dateEnd   ?? undefined;
        return {
            dateFrom: dateFrom ? String(dateFrom) : undefined,
            dateTo:   dateTo   ? String(dateTo)   : undefined,
        };
    }

    //Build query object for routing to yacht page using an offer
    function buildOfferQuery(offer: any, fallbackTrip?: Trip | null): Record<string, string> {
        const query: Record<string, string> = {};
        const { dateFrom, dateTo } = resolveOfferDates(offer, fallbackTrip ?? trip.value);
        if (dateFrom) query.dateFrom = dateFrom;
        if (dateTo)   query.dateTo   = dateTo;

        const amenitiesRaw = offer?.amenities ?? offer?.selectedAmenities ?? offer?.filters?.amenities ?? offer?.yacht?.amenities ?? null;
        const amenitiesIds = normalizeAmenities(amenitiesRaw);
        if (amenitiesIds.length) query.amenities = amenitiesIds.join(',');

        return query;
    }

    // Fetch a single offer within a trip (by yacht UUID)
    async function getTripOfferYacht(uuid: string, yachtUuid: string) {
        try {
            const res = await TripService.getTripOfferDetail(uuid, yachtUuid);
            console.log("getTripOffer response:", res?.data);
            return res?.data;
        } catch (error) {
            console.error(`[getTripOffer] Failed for trip ${uuid} and yacht ${yachtUuid}:`, error);
            throw error;
        }
    }

    return {
        loading,
        trips,
        trip,
        boatsInTrips,
        tripImageUrl,
        getAvailable,
        hasTrips,
        getAll,
        loadTrip,
        createTrip,
        removeTrip,
        patchOfferToTrip,
        deleteTripOffer,
        getTripStatus,
        updateTrip,
        updateTripImageStore,
        removeCrewMember,
        updateTripCrew,
        isYachtInAnyTrip,
        isYachtInTripByUuid,
        normalizeAmenities,
        resolveOfferDates,
        buildOfferQuery,
        getTripOfferYacht,
        updateTripOffer,
        payload
    };
});